import { useEffect, useCallback } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  canonical?: string;
  noindex?: boolean;
  structuredData?: object | object[];
  breadcrumbs?: { name: string; url: string }[];
}

interface MetaTagConfig {
  name?: string;
  property?: string;
  content: string;
}

const BASE_URL = 'https://snugglecrafters.com';
const SITE_NAME = 'SnuggleCrafters';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const TWITTER_HANDLE = '@snugglecrafters';

/**
 * Custom hook for managing SEO meta tags dynamically in a React SPA.
 * Handles title, meta tags, Open Graph, Twitter Cards, canonical URLs,
 * and structured data (JSON-LD).
 */
export function useSEO(config: SEOConfig) {
  const updateMetaTag = useCallback((selector: string, attrs: MetaTagConfig) => {
    let element = document.querySelector(selector) as HTMLMetaElement | null;

    if (!element) {
      element = document.createElement('meta');
      if (attrs.name) element.setAttribute('name', attrs.name);
      if (attrs.property) element.setAttribute('property', attrs.property);
      document.head.appendChild(element);
    }

    element.setAttribute('content', attrs.content);
  }, []);

  const updateLinkTag = useCallback((rel: string, href: string, attrs?: Record<string, string>) => {
    let selector = `link[rel="${rel}"]`;
    if (attrs?.hreflang) {
      selector = `link[rel="${rel}"][hreflang="${attrs.hreflang}"]`;
    }

    let element = document.querySelector(selector) as HTMLLinkElement | null;

    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      if (attrs) {
        Object.entries(attrs).forEach(([key, value]) => {
          element!.setAttribute(key, value);
        });
      }
      document.head.appendChild(element);
    }

    element.setAttribute('href', href);
  }, []);

  const updateStructuredData = useCallback((data: object | object[], id: string = 'seo-structured-data') => {
    // Remove existing structured data script
    const existing = document.getElementById(id);
    if (existing) {
      existing.remove();
    }

    // Create new script element
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';

    // Handle array of structured data or single object
    if (Array.isArray(data)) {
      script.textContent = JSON.stringify(data);
    } else {
      script.textContent = JSON.stringify(data);
    }

    document.head.appendChild(script);
  }, []);

  const updateBreadcrumbs = useCallback((breadcrumbs: { name: string; url: string }[]) => {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": index === breadcrumbs.length - 1 ? undefined : `${BASE_URL}${item.url}`
      }))
    };

    updateStructuredData(breadcrumbData, 'seo-breadcrumbs');
  }, [updateStructuredData]);

  useEffect(() => {
    const {
      title,
      description,
      keywords,
      image = DEFAULT_IMAGE,
      url,
      type = 'website',
      canonical,
      noindex = false,
      structuredData,
      breadcrumbs
    } = config;

    const fullUrl = url ? (url.startsWith('http') ? url : `${BASE_URL}${url}`) : window.location.href;
    const canonicalUrl = canonical || fullUrl;

    // Update document title
    document.title = title;

    // Primary meta tags
    updateMetaTag('meta[name="title"]', { name: 'title', content: title });
    updateMetaTag('meta[name="description"]', { name: 'description', content: description });

    if (keywords) {
      updateMetaTag('meta[name="keywords"]', { name: 'keywords', content: keywords });
    }

    // Robots
    const robotsContent = noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1';
    updateMetaTag('meta[name="robots"]', { name: 'robots', content: robotsContent });
    updateMetaTag('meta[name="googlebot"]', { name: 'googlebot', content: robotsContent });

    // Open Graph
    updateMetaTag('meta[property="og:type"]', { property: 'og:type', content: type });
    updateMetaTag('meta[property="og:url"]', { property: 'og:url', content: fullUrl });
    updateMetaTag('meta[property="og:title"]', { property: 'og:title', content: title });
    updateMetaTag('meta[property="og:description"]', { property: 'og:description', content: description });
    updateMetaTag('meta[property="og:image"]', { property: 'og:image', content: image });
    updateMetaTag('meta[property="og:image:alt"]', { property: 'og:image:alt', content: title });
    updateMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    updateMetaTag('meta[property="og:locale"]', { property: 'og:locale', content: 'en_US' });

    // Twitter Card
    updateMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    updateMetaTag('meta[name="twitter:site"]', { name: 'twitter:site', content: TWITTER_HANDLE });
    updateMetaTag('meta[name="twitter:creator"]', { name: 'twitter:creator', content: TWITTER_HANDLE });
    updateMetaTag('meta[name="twitter:url"]', { name: 'twitter:url', content: fullUrl });
    updateMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    updateMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    updateMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    updateMetaTag('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: title });

    // Canonical URL
    updateLinkTag('canonical', canonicalUrl);

    // Structured data
    if (structuredData) {
      updateStructuredData(structuredData);
    }

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      updateBreadcrumbs(breadcrumbs);
    }

    // Cleanup function - restore defaults on unmount (optional)
    return () => {
      // Optionally clean up custom structured data scripts
      const customScript = document.getElementById('seo-structured-data');
      if (customScript) {
        customScript.remove();
      }
      const breadcrumbScript = document.getElementById('seo-breadcrumbs');
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
    };
  }, [config, updateMetaTag, updateLinkTag, updateStructuredData, updateBreadcrumbs]);
}

// Pre-defined SEO configurations for common pages
export const SEO_CONFIGS = {
  home: {
    title: 'AI Bedtime Stories for Kids | Personalized Stories | SnuggleCrafters',
    description: 'Create magical, personalized AI bedtime stories for your child. Safe, educational storytelling that sparks imagination. Try free today!',
    keywords: 'AI bedtime stories, personalized kids stories, bedtime story app, children storytelling AI, safe AI stories for kids',
    url: '/',
    type: 'website' as const,
  },
  about: {
    title: 'About SnuggleCrafters | AI Bedtime Story Creators',
    description: 'Learn how SnuggleCrafters uses safe, child-friendly AI to create personalized bedtime stories. Our mission is to make every child\'s bedtime magical.',
    keywords: 'about snugglecrafters, AI storytelling company, bedtime story creators, children education technology',
    url: '/about',
    type: 'website' as const,
  },
  features: {
    title: 'Features | AI Story Generator, Audio Narration & More | SnuggleCrafters',
    description: 'Discover SnuggleCrafters features: AI story generation, professional audio narration, beautiful illustrations, multi-language support, and safe content.',
    keywords: 'AI story features, bedtime story generator, audio narration, illustrated stories, multi-language stories',
    url: '/features',
    type: 'website' as const,
  },
  pricing: {
    title: 'Pricing Plans | Affordable AI Bedtime Stories | SnuggleCrafters',
    description: 'Choose the perfect SnuggleCrafters plan for your family. Start free, upgrade anytime. Unlimited AI stories, audio, and illustrations.',
    keywords: 'bedtime story subscription, story app pricing, family subscription, unlimited stories',
    url: '/pricing',
    type: 'website' as const,
  },
  stories: {
    title: 'Bedtime Story Library | Browse AI-Generated Stories | SnuggleCrafters',
    description: 'Explore your personalized bedtime story library. Read, listen, and share magical AI-generated tales created just for your child.',
    keywords: 'story library, bedtime stories collection, personalized stories, AI generated stories',
    url: '/stories',
    type: 'website' as const,
  },
  characters: {
    title: 'Story Characters | Create Custom Characters | SnuggleCrafters',
    description: 'Create and customize characters for your bedtime stories. Add your child\'s favorite characters or create new magical friends.',
    keywords: 'story characters, custom characters, children characters, personalized characters, story protagonists',
    url: '/characters',
    type: 'website' as const,
  },
  parents: {
    title: 'For Parents | Safety, Privacy & Trust | SnuggleCrafters',
    description: 'Learn how SnuggleCrafters keeps your children safe. COPPA-compliant, ad-free, and designed with families in mind.',
    keywords: 'parental controls, child safety, COPPA compliant, safe kids app, privacy for children',
    url: '/parents',
    type: 'website' as const,
  },
  help: {
    title: 'Help Center | FAQs & Support | SnuggleCrafters',
    description: 'Get help with SnuggleCrafters. Find answers to common questions about creating stories, managing accounts, and using our app.',
    keywords: 'help center, FAQ, support, customer service, how to use',
    url: '/help',
    type: 'website' as const,
  },
  contact: {
    title: 'Contact Us | Customer Support | SnuggleCrafters',
    description: 'Contact the SnuggleCrafters team. We\'re here to help with questions, feedback, and support requests.',
    keywords: 'contact us, customer support, help, feedback, support request',
    url: '/contact',
    type: 'website' as const,
  },
  marketplace: {
    title: 'Story Merchandise | Custom Books & Gifts | SnuggleCrafters',
    description: 'Turn your favorite stories into physical books, prints, and keepsakes. Custom merchandise featuring your child\'s personalized tales.',
    keywords: 'story merchandise, custom books, personalized gifts, story prints, children keepsakes',
    url: '/marketplace',
    type: 'website' as const,
  },
  app: {
    title: 'Download SnuggleCrafters App | iOS & Android | Bedtime Stories',
    description: 'Download the SnuggleCrafters app for iOS and Android. Create personalized AI bedtime stories anywhere, anytime.',
    keywords: 'bedtime story app, iOS app, Android app, children app, storytelling app download',
    url: '/app',
    type: 'website' as const,
  },
  blog: {
    title: 'Parenting Blog | Bedtime Tips & Stories | SnuggleCrafters',
    description: 'Expert parenting tips, bedtime routine advice, and storytelling insights from the SnuggleCrafters team.',
    keywords: 'parenting blog, bedtime tips, storytelling advice, children sleep, parenting advice',
    url: '/blog',
    type: 'website' as const,
  },
  privacy: {
    title: 'Privacy Policy | SnuggleCrafters',
    description: 'Read SnuggleCrafters privacy policy. Learn how we protect your family\'s data and maintain COPPA compliance.',
    keywords: 'privacy policy, data protection, COPPA compliance, children privacy',
    url: '/privacy',
    type: 'website' as const,
  },
  terms: {
    title: 'Terms of Service | SnuggleCrafters',
    description: 'SnuggleCrafters terms of service. Understand our user agreement and service policies.',
    keywords: 'terms of service, user agreement, service terms',
    url: '/terms',
    type: 'website' as const,
  },
  subscribe: {
    title: 'Subscribe | Start Creating AI Stories | SnuggleCrafters',
    description: 'Subscribe to SnuggleCrafters and unlock unlimited personalized bedtime stories for your family.',
    keywords: 'subscribe, subscription, family plan, unlimited stories',
    url: '/subscribe',
    type: 'website' as const,
  },
  giftSubscription: {
    title: 'Gift a Subscription | SnuggleCrafters',
    description: 'Give the gift of magical bedtime stories. Purchase a SnuggleCrafters gift subscription for friends and family.',
    keywords: 'gift subscription, gift card, children gift, family gift, story gift',
    url: '/gift-subscription',
    type: 'website' as const,
  },
  community: {
    title: 'Community | Join SnuggleCrafters Families',
    description: 'Join the SnuggleCrafters community. Connect with other parents, share stories, and discover new bedtime ideas.',
    keywords: 'community, parents community, storytelling community, family stories',
    url: '/community',
    type: 'website' as const,
  },
  developer: {
    title: 'Developer Portal | SnuggleCrafters API',
    description: 'Access the SnuggleCrafters API. Build integrations and applications using our storytelling technology.',
    keywords: 'developer portal, API access, integration, storytelling API',
    url: '/developer',
    type: 'website' as const,
  },
};

// Helper to generate story-specific SEO config
export function getStorySEOConfig(story: {
  id: number;
  title: string;
  content?: string;
  illustrationUrl?: string;
  age?: number;
  interests?: string[];
}): SEOConfig {
  const description = story.content
    ? story.content.substring(0, 150).trim() + '...'
    : `Read "${story.title}" - a magical personalized bedtime story created just for you on SnuggleCrafters.`;

  return {
    title: `${story.title} | SnuggleCrafters Story`,
    description,
    keywords: [
      'personalized story',
      'AI bedtime story',
      'custom children story',
      ...(story.interests || [])
    ].join(', '),
    image: story.illustrationUrl || DEFAULT_IMAGE,
    url: `/story/${story.id}`,
    type: 'article',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "name": story.title,
      "author": {
        "@type": "Organization",
        "name": "SnuggleCrafters"
      },
      "description": description,
      "genre": "Children's Literature",
      "inLanguage": "en",
      "keywords": story.interests?.join(', '),
      "audience": {
        "@type": "PeopleAudience",
        "audienceType": "Children",
        "suggestedMinAge": Math.max(2, (story.age || 5) - 2),
        "suggestedMaxAge": Math.min(12, (story.age || 5) + 2)
      },
      "isFamilyFriendly": true
    },
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Stories', url: '/stories' },
      { name: story.title, url: `/story/${story.id}` }
    ]
  };
}

// Helper to generate blog post SEO config
export function getBlogPostSEOConfig(post: {
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  publishedAt?: string;
  author?: string;
}): SEOConfig {
  return {
    title: `${post.title} | SnuggleCrafters Blog`,
    description: post.excerpt,
    keywords: 'parenting, bedtime stories, children, storytelling, tips',
    image: post.image || DEFAULT_IMAGE,
    url: `/blog/${post.slug}`,
    type: 'article',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image || DEFAULT_IMAGE,
      "datePublished": post.publishedAt || new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": post.author || "SnuggleCrafters"
      },
      "publisher": {
        "@type": "Organization",
        "name": "SnuggleCrafters",
        "logo": {
          "@type": "ImageObject",
          "url": `${BASE_URL}/logo.png`
        }
      }
    },
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blog' },
      { name: post.title, url: `/blog/${post.slug}` }
    ]
  };
}

export default useSEO;
